import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EmptyState, IconButton, Page, PageHeader, Segmented, TableSkeleton } from "../../../components/ui";
import { getCategoryStats } from "../../../services/categoryStats";
import { formatDuration } from "../video-performance/VideoPerformance";
import { fmt, FormatFilter, MoodBar, PeriodFilter, usePeriod, Versus } from "./shared";
import "../payments-ledger/payments.css";
import "../video-performance/video-performance.css";
import "./category-stats.css";

const SORTS = [
  { value: "views", label: "Views" },
  { value: "trend", label: "Biggest rise" },
  { value: "completion", label: "Watched to the end" },
  { value: "reactionRate", label: "Reactions per 100 views" },
  { value: "sentiment", label: "Sentiment" },
  { value: "name", label: "Name" },
];
const sortValue = {
  views: (c) => c.views,
  trend: (c) => (c.trend === null ? -Infinity : c.trend),
  completion: (c) => (c.completion === null ? -1 : c.completion),
  reactionRate: (c) => (c.reactionRate === null ? -1 : c.reactionRate),
  sentiment: (c) => (c.sentiment?.score === null || c.sentiment?.score === undefined ? -101 : c.sentiment.score),
};
const TONES = { good: { label: "In good shape", color: "green" }, bad: { label: "To watch", color: "red" }, mixed: { label: "Mixed", color: "slate" } };

export const sortCategories = (list, sort) =>
  sort === "name" ? [...list].sort((a, b) => a.name.localeCompare(b.name)) : [...list].sort((a, b) => sortValue[sort](b) - sortValue[sort](a) || b.views - a.views);

const Trend = ({ value }) => {
  const { t } = useTranslation();
  if (value === null || value === undefined) return null;
  const tone = value > 0 ? "up" : value < 0 ? "down" : "flat";
  return (
    <small className="perf-delta" data-tone={tone} title={t("vs previous period")}>
      <i className={tone === "up" ? "ri-arrow-up-line" : tone === "down" ? "ri-arrow-down-line" : "ri-subtract-line"} aria-hidden="true"></i>
      {Math.abs(value)} %
    </small>
  );
};

/**
 * Every rubric (category) over a period: audience, how far people watch, reactions per 100 views,
 * comment sentiment, and a verdict against the platform. A rubric opens its own page.
 */
const CategoryStats = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const period = usePeriod(location, history);
  const [view, setView] = useState("cards");
  const [sort, setSort] = useState("views");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const paramsKey = JSON.stringify(period.params);

  const load = useCallback(
    (fresh) => {
      const params = JSON.parse(paramsKey);
      if (!params) return;
      setData(null);
      setError(false);
      getCategoryStats({ ...params, ...(fresh === true ? { fresh: 1 } : {}) })
        .then(setData)
        .catch(() => setError(true));
    },
    [paramsKey]
  );
  useEffect(() => load(), [load]);

  const open = (id) => history.push(`/dashboard/category-stats/${id}${period.search}`);
  const p = data?.platform;
  const list = data ? sortCategories(data.categories, sort) : [];

  return (
    <Page>
      <PageHeader
        title={t("Stats by rubric")}
        subtitle={t("How each rubric performs: audience, how far people watch, reactions, what the comments say, and where to improve.")}
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={() => load(true)} />}
      />

      <div className="perf-filters">
        <FormatFilter state={period} />
        <PeriodFilter state={period} />
        {data?.computedAt && (
          <span className="ui-hint perf-computed">{t("Figures of {{time}}", { time: new Date(data.computedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}</span>
        )}
      </div>

      <div className="ledger-stats perf-stats">
        <div className="ui-card ledger-stat">
          <strong>{p ? fmt(p.views) : "—"}</strong>
          <span>{t("Views on the platform")}</span>
          {p?.trend !== null && p?.trend !== undefined && <Trend value={p.trend} />}
        </div>
        <div className="ui-card ledger-stat">
          <strong>{p?.completion !== null && p?.completion !== undefined ? `${p.completion} %` : "—"}</strong>
          <span>{t("Plays watched to the end")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{p?.reactionRate ?? "—"}</strong>
          <span>{t("Reactions per 100 views")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{p?.sentiment?.score !== null && p?.sentiment?.score !== undefined ? p.sentiment.score : "—"}</strong>
          <span>{t("Sentiment index of the comments")}</span>
          <small className="ui-hint">{t("from −100 (all negative) to +100 (all positive)")}</small>
        </div>
      </div>

      <section className="ui-card">
        <div className="ui-toolbar ledger-toolbar">
          <Segmented
            label={t("View")}
            value={view}
            onChange={setView}
            options={[
              { value: "cards", label: t("Cards") },
              { value: "table", label: t("Table") },
            ]}
          />
          <select className="ui-input perf-sort" aria-label={t("Rank by")} value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {t("Rank by")} : {t(s.label)}
              </option>
            ))}
          </select>
        </div>

        {error ? (
          <EmptyState icon="ri-error-warning-line">
            {t("The list could not be loaded.")}{" "}
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => load(true)}>
              {t("Retry")}
            </button>
          </EmptyState>
        ) : !data ? (
          <TableSkeleton />
        ) : !list.length ? (
          <EmptyState icon="ri-price-tag-3-line">{t("No rubric with content")}</EmptyState>
        ) : view === "cards" ? (
          <div className="cat-grid">
            {list.map((c) => {
              const tone = TONES[c.insights?.tone] || TONES.mixed;
              return (
                <button type="button" key={c.id} className="ui-card cat-card" onClick={() => open(c.id)}>
                  <span className="cat-card-head">
                    {c.image ? <img src={c.image} alt="" /> : <span className="cat-card-icon" aria-hidden="true"><i className="ri-price-tag-3-line"></i></span>}
                    <span className="cat-card-title">
                      <strong>{c.name}</strong>
                      <span className="ui-hint">
                        {t("{{count}} videos", { count: c.videos, formatted: fmt(c.videos) })}
                        {c.series ? ` · ${t("{{count}} series", { count: c.series })}` : ""}
                      </span>
                    </span>
                    <span className={`cat-tone`} data-tone={c.insights?.tone || "mixed"}>{t(tone.label)}</span>
                  </span>
                  <span className="cat-card-views">
                    <strong>{fmt(c.views)}</strong> {t("views")} <Trend value={c.trend} />
                  </span>
                  <span className="cat-card-figures">
                    <span>
                      <strong>{c.completion !== null ? `${c.completion} %` : "—"}</strong>
                      {t("to the end")}
                      <Versus value={c.completion} reference={p.completion} unit="pts" label=" " />
                    </span>
                    <span>
                      <strong>{c.reactionRate ?? "—"}</strong>
                      {t("reactions / 100 views")}
                      <Versus value={c.reactionRate} reference={p.reactionRate} label=" " />
                    </span>
                    <span>
                      <strong>{c.watchSeconds ? formatDuration(c.watchSeconds) : "—"}</strong>
                      {t("watched")}
                    </span>
                  </span>
                  <MoodBar mood={c.sentiment} compact />
                  {c.insights?.headline && <span className="cat-card-headline">{c.insights.headline}</span>}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table perf-table">
              <thead>
                <tr>
                  <th className="perf-sticky perf-sticky-rank">#</th>
                  <th className="perf-sticky perf-sticky-title">{t("Rubric")}</th>
                  <th>{t("Views")}</th>
                  <th>{t("Views per video")}</th>
                  <th>{t("Watched to the end")}</th>
                  <th>{t("Left in the first 10 %")}</th>
                  <th>{t("Reactions per 100 views")}</th>
                  <th>{t("Comments")}</th>
                  <th>{t("Likes")}</th>
                  <th>{t("Sentiment")}</th>
                  <th>{t("Published")}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c, i) => (
                  <tr key={c.id} className="ledger-row" onClick={() => open(c.id)}>
                    <td className="ui-cell-muted perf-sticky perf-sticky-rank">{i + 1}</td>
                    <td className="perf-sticky perf-sticky-title">
                      <strong>{c.name}</strong>
                      <span className="ui-media-meta">
                        {t("{{count}} videos", { count: c.videos, formatted: fmt(c.videos) })}
                        {c.series ? ` · ${t("{{count}} series", { count: c.series })}` : ""}
                      </span>
                    </td>
                    <td>
                      <span className="perf-views">
                        <strong>{fmt(c.views)}</strong>
                        <Trend value={c.trend} />
                      </span>
                    </td>
                    <td>{fmt(c.viewsPerVideo)}</td>
                    <td>{c.completion !== null ? `${c.completion} %` : "—"}</td>
                    <td>{c.earlyDrop !== null ? `${c.earlyDrop} %` : "—"}</td>
                    <td>{c.reactionRate ?? "—"}</td>
                    <td>{fmt(c.comments + c.replies)}</td>
                    <td>{fmt(c.likes)}</td>
                    <td className="cat-mood-cell">
                      <MoodBar mood={c.sentiment} compact />
                    </td>
                    <td>{fmt(c.published)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="ui-hint perf-hint">
          {t("Reactions: comments, replies, likes, shares and Merci. Sentiment: comments read automatically (French and English words, emojis, negations); an indication over many comments, not a judgement on one. The episodes of the series of a rubric count for plays and comments; views come from the videos.")}
        </p>
      </section>
    </Page>
  );
};

export default CategoryStats;
